import React from 'react';
import { ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * OrderSummary - Condensed cart preview for payment page
 * 
 * Features:
 * - Expandable/collapsible item list
 * - Item count + total always visible
 * - Modifier display when expanded
 */
export default function OrderSummary({
  cart = [],
  itemCount = 0,
  isExpanded = false,
  onToggle,
  className = '',
}) {
  if (!cart.length) return null;

  return (
    <div className={`bg-[#1a1f26] border border-white/10 rounded-2xl overflow-hidden ${className}`}>
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="
          w-full h-14 px-4
          flex items-center justify-between
          hover:bg-white/5
          transition-colors
        "
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#f5a623]/10 flex items-center justify-center">
            <ShoppingBag size={18} className="text-[#f5a623]" />
          </div>
          <span className="font-medium text-white">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">
            {isExpanded ? 'Hide details' : 'View details'}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 animate-slide-down">
          <div className="space-y-3">
            {cart.map((item) => (
              <div 
                key={item.id}
                className="flex items-start justify-between py-2 border-t border-white/5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-white">
                      {item.quantity}×
                    </span>
                    <span className="text-sm text-white truncate">
                      {item.name}
                    </span>
                  </div>
                  {item.modifiers && item.modifiers.length > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5 pl-6 truncate">
                      {item.modifiers.map(m => m.name).join(', ')}
                    </p>
                  )}
                </div>
                <span className="text-sm text-gray-400 ml-3">
                  {formatCurrency((item.totalPrice || item.price) * item.quantity)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Animation */}
      <style>{`
        @keyframes slide-down {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 500px; }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
